import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';

import { getCertificateWithInstallmentsById } from '@share/api';
import {
  CertificateStatusEnum,
  isInstalmentWalletPaymentType,
  type IInstalmentSchedule,
} from '@share/common-types';
import { Toaster } from '@share/utils';

import { getCertificateProblemDetailsErrorMessage } from '../../../components/payment-form/utils/certificate-problem-details-error';

export interface UseWalletCardInstallmentsParams {
  certificateId: string;
  paymentType: string;
  status: CertificateStatusEnum;
  isExpanded: boolean;
  /** Shown when `refetchInstallments` fails (non-cancel). */
  installmentRefetchErrorMessage: string;
}

export interface UseWalletCardInstallmentsResult {
  fetchedInstallmentSchedule: IInstalmentSchedule | undefined;
  fetchedCertificateTotalPrice: number | undefined;
  isInstallmentsLoading: boolean;
  shouldFetchInstallments: boolean;
  refetchInstallments: () => Promise<void>;
}

export function useWalletCardInstallments(
  params: UseWalletCardInstallmentsParams,
): UseWalletCardInstallmentsResult {
  const { certificateId, paymentType, status, isExpanded, installmentRefetchErrorMessage } = params;

  const [fetchedInstallmentSchedule, setFetchedInstallmentSchedule] = useState<
    IInstalmentSchedule | undefined
  >(undefined);
  const [fetchedCertificateTotalPrice, setFetchedCertificateTotalPrice] = useState<
    number | undefined
  >(undefined);
  const [isInstallmentsLoading, setIsInstallmentsLoading] = useState<boolean>(false);
  const refetchAbortRef = useRef<AbortController | null>(null);

  const shouldFetchInstallments: boolean = useMemo(
    () => status === CertificateStatusEnum.Pending && isInstalmentWalletPaymentType(paymentType),
    [paymentType, status],
  );

  const abortPendingRefetch = useCallback((): void => {
    refetchAbortRef.current?.abort();
    refetchAbortRef.current = null;
  }, []);

  const refetchInstallments = useCallback(async (): Promise<void> => {
    if (!isInstalmentWalletPaymentType(paymentType)) {
      return;
    }
    abortPendingRefetch();
    const abortController = new AbortController();
    refetchAbortRef.current = abortController;
    setIsInstallmentsLoading(true);
    try {
      const { data } = await getCertificateWithInstallmentsById(certificateId, {
        signal: abortController.signal,
      });
      if (refetchAbortRef.current === abortController) {
        setFetchedInstallmentSchedule(data.instalmentSchedule);
      }
    } catch (error: unknown) {
      if (axios.isAxiosError(error) && error.code === 'ERR_CANCELED') {
        return;
      }
      if (refetchAbortRef.current === abortController) {
        Toaster.error(
          getCertificateProblemDetailsErrorMessage(error, installmentRefetchErrorMessage),
        );
      }
    } finally {
      if (refetchAbortRef.current === abortController) {
        setIsInstallmentsLoading(false);
        refetchAbortRef.current = null;
      }
    }
  }, [abortPendingRefetch, certificateId, installmentRefetchErrorMessage, paymentType]);

  useEffect(() => {
    return (): void => {
      abortPendingRefetch();
    };
  }, [abortPendingRefetch]);

  useEffect(() => {
    if (!isExpanded) {
      abortPendingRefetch();
      setFetchedInstallmentSchedule(undefined);
      setFetchedCertificateTotalPrice(undefined);
      setIsInstallmentsLoading(false);
      return;
    }

    if (!shouldFetchInstallments) {
      return;
    }

    const abortController = new AbortController();
    setIsInstallmentsLoading(true);
    setFetchedInstallmentSchedule(undefined);
    setFetchedCertificateTotalPrice(undefined);

    void getCertificateWithInstallmentsById(certificateId, { signal: abortController.signal })
      .then((response) => {
        setFetchedInstallmentSchedule(response.data.instalmentSchedule);
        setFetchedCertificateTotalPrice(response.data.totalPrice);
      })
      .catch((error: unknown) => {
        if (axios.isAxiosError(error) && error.code === 'ERR_CANCELED') {
          return;
        }
        setFetchedInstallmentSchedule(undefined);
        setFetchedCertificateTotalPrice(undefined);
      })
      .finally(() => {
        setIsInstallmentsLoading(false);
      });

    return () => {
      abortController.abort();
    };
  }, [abortPendingRefetch, certificateId, isExpanded, shouldFetchInstallments]);

  return {
    fetchedInstallmentSchedule,
    fetchedCertificateTotalPrice,
    isInstallmentsLoading,
    shouldFetchInstallments,
    refetchInstallments,
  };
}
